import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Link, Typography } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import PropTypes from 'prop-types'

import AuthAPI from '../../../api/orbitV1/AuthAPI'
import UserAPI from '../../../api/orbitV1/UserAPI'
import VersionAPI from '../../../api/orbitV1/VersionAPI'
import { ERR_UNEXPECTED } from '../../../constants/errors'
import useAuth from '../../../hooks/useAuth'
import OrbitSession from '../../../hooks/useCookies'
import useIdentity from '../../../hooks/useIdentity'

import OtpChain from './OtpChain'

const OtpForm = (props) => {
  const { email, executeRecaptcha } = props
  const navigate = useNavigate()
  const { setAuth } = useAuth()

  const OTP_TIMELIMIT = 60
  const [otp, setOtp] = useState('')
  const [otpError, setOtpError] = useState(undefined)
  const [timeLeft, setTimeLeft] = useState(OTP_TIMELIMIT)

  const { setIdentity } = useIdentity()

  const { mutateAsync: logUserIn } = useMutation(async ({ email, otp }) => {
    const { data } = await AuthAPI.logUserIn(email, otp)
    return data
  })

  useEffect(() => {
    if (timeLeft <= 0) {
      return
    }

    const timer = setInterval(() => {
      setTimeLeft(timeLeft - 1)
    }, 1000)

    return () => clearTimeout(timer)
  }, [timeLeft])

  const handleOtpSubmit = async (e) => {
    e.preventDefault()
    setOtpError('')

    try {
      await logUserIn(
        { email, otp },
        {
          onError: (error) => {
            if (error.response.status === 401) {
              if (timeLeft > 0) {
                setOtpError('Invalid OTP. Please enter the correct OTP')
              } else {
                setOtpError('Please click Resend OTP')
              }
            } else {
              setOtpError(ERR_UNEXPECTED)
            }
          },
          onSuccess: async (response) => {
            const userDetails = await UserAPI.getUserByEmail(email)
            const ver = await VersionAPI.getVersion()

            if (userDetails?.data && userDetails.data.length > 0) {
              // Faber token to be used to query backend
              const { data } = response
              const faberToken = `${data.tokenString}`

              const user = userDetails.data[0]

              try {
                const profilePic = await UserAPI.getPfpByUserId(user._id)
                if (profilePic.data?.photoUrl) {
                  OrbitSession.setStoreType('localstorage')
                  OrbitSession.set({ profileImg: profilePic.data.photoUrl })
                  OrbitSession.setStoreType('cookie')
                }
              } catch (error) {
                setOtpError('Something went wrong. Please try again.')
              }

              OrbitSession.set({
                email: user.email,
                role: user.role,
                orgId: user.orgId,
                _id: user._id,
                faberToken,
                userName: user.name,
                backendVersion: ver.data,
                firstLogin: user.firstLogin,
                contactNumber: user.contactNumber,
                joyRideVersions: user.joyRideVersions
              })

              if (user.firstLogin) {
                await UserAPI.updateFirstLogin(user)
              }

              setAuth({ role: [user.role] })
              setIdentity({
                email: user.email,
                role: user.role,
                orgId: user.orgId,
                _id: user._id,
                userName: user.name,
                backendVersion: ver.data,
                firstLogin: user.firstLogin,
                contactNumber: user.contactNumber
              })

              if (['owner', 'admin'].includes(user.role)) {
                navigate('/app', { state: {} })
              }

              if (['opsowner', 'ops', 'opsviewer'].includes(user.role)) {
                navigate('/hq', { state: {} })
              }
            }
          }
          // onSettled: {}
        }
      )
    } catch (error) {
      setOtpError('Invalid OTP. Please enter the correct OTP')
    }
  }

  /* Resend OTP handler */
  const handleReOTP = async () => {
    const action = `login_${window.location.hostname.replace(/(\.|-)/g, '_')}`
    const captchaToken = await executeRecaptcha(action)

    try {
      await AuthAPI.getOtpByEmail(email, captchaToken)
    } catch (error) {
      setOtpError('Something went wrong. Please try again.')
    }

    setTimeLeft(OTP_TIMELIMIT)
  }

  const handleEmailReset = () => {
    setOtp('')
    setOtpError('')
    setTimeLeft(0)
    props.onReset()
  }

  const otpValid = timeLeft > 0

  return (
    <>
      <Box sx={{ marginTop: '40px', marginBottom: '30px', width: '100%' }}>
        <Typography sx={{ color: '#818D9C' }}>
          Please check your corporate email for the OTP
        </Typography>
        <Typography variant='subtitle1' sx={{ color: '#818D9C' }}>
          <i>{otpValid ? `(OTP expiring in ${timeLeft}s)` : '(OTP Expired)'}</i>
        </Typography>
      </Box>

      <Box display='flex' alignItems='center' justifyContent='space-between'>
        <Typography variant='h6'>Please enter 6 digit OTP</Typography>
        <Link
          sx={{
            fontSize: '14px'
          }}
          {...(otpValid
            ? {
                sx: {
                  fontSize: '14px',
                  cursor: 'auto',
                  color: 'gray',
                  border: 'none',
                  ':hover': {
                    textDecoration: 'none'
                  }
                }
              }
            : { onClick: handleReOTP })}
        >
          Resend OTP
        </Link>
      </Box>

      <form onSubmit={handleOtpSubmit}>
        <Box>
          <OtpChain autoFocus length={6} setOtp={setOtp} />
          <Box sx={{ marginY: '20px' }}>
            <Typography
              sx={{
                height: '1em',
                fontSize: '12px',
                color: 'red'
              }}
            >
              <i>{otpError}</i>
            </Typography>
          </Box>
          <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            width='100%'
          >
            <Button
              data-cy='verifyOTPBtn'
              variant='filled'
              id='verifyOTPButton'
              aria-label='verify otp'
              type='submit'
              sx={{ width: '100%' }}
            >
              Verify OTP
            </Button>
          </Box>
        </Box>
      </form>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          marginBottom: '60px'
        }}
      >
        <Button
          id='tryAnotherEmailButton'
          aria-label='try another email'
          variant='filled'
          onClick={handleEmailReset}
          sx={{ width: '100%' }}
        >
          Try another email
        </Button>
      </Box>
    </>
  )
}

OtpForm.propTypes = {
  email: PropTypes.string,
  executeRecaptcha: PropTypes.func,
  onReset: PropTypes.func
}

export default OtpForm
